import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import LoadingSpinner from "../../components/common/LoadingSpinner";
import WelcomeModal from "../../components/common/WelcomeModal";
import { EyeIcon, EyeSlashIcon } from "@heroicons/react/24/outline";
import { ThemeToggle } from "../../components/ThemeToggle";

export default function LoginPage() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [showWelcomeModal, setShowWelcomeModal] = useState(false);

  const { login, state } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const from = (location.state as any)?.from?.pathname || "/";

  // Mostrar modal de boas-vindas apenas na primeira visita
  useEffect(() => {
    if (!state.isAuthenticated && !state.isLoading) {
      // Verificar se já viu o modal
      const hasSeenWelcome = localStorage.getItem('hubmachines_welcome_seen');
      if (!hasSeenWelcome) {
        // Pequeno delay para melhor UX (permite que a página carregue primeiro)
        const timer = setTimeout(() => {
          setShowWelcomeModal(true);
        }, 500);
        
        return () => clearTimeout(timer);
      }
    }
  }, [state.isAuthenticated, state.isLoading]);

  // Navegar apenas após login bem-sucedido (não durante erro)
  useEffect(() => {
    // Só navegar se estiver autenticado, não estiver carregando, não houver erro e não estiver na página de login
    if (state.isAuthenticated && !state.isLoading && !isLoading && !error) {
      navigate(from, { replace: true });
    }
  }, [state.isAuthenticated, state.isLoading, isLoading, error, navigate, from]);

  const handleCloseWelcome = () => {
    // Salvar no localStorage que o usuário já viu o modal
    localStorage.setItem('hubmachines_welcome_seen', 'true');
    setShowWelcomeModal(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setIsLoading(true);

    try {
      await login(email, password);
      // Login bem-sucedido - resetar loading e navegação será feita pelo useEffect
      setIsLoading(false);
    } catch (err: any) {
      // Extrair mensagem de erro do backend
      const errorMessage = err.response?.data?.message || 
                          err.response?.data?.error ||
                          "Usuário ou senha inválidos";
      setError(errorMessage);
      // Garantir que o estado de loading seja desativado
      setIsLoading(false);
      // Não navegar em caso de erro - deixar mensagem visível
    }
  };

  if (state.isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center dark:bg-gray-900">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  const backgroundStyle = {
    backgroundImage: `url("/login-bg.jpg")`,
    backgroundSize: "cover",
    backgroundPosition: "center",
    backgroundRepeat: "no-repeat",
  };

  return (
    <>
      {showWelcomeModal && <WelcomeModal onClose={handleCloseWelcome} />}
      <div
        className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 py-12 px-4 sm:px-6 lg:px-8 transition-colors duration-200"
        style={backgroundStyle}
      >
      <div className="max-w-2xl w-full space-y-10 bg-white/10 dark:bg-black/20 p-12 rounded-2xl shadow-2xl backdrop-blur-sm border border-gray-200 dark:border-gray-800 relative">
        <div className="text-center relative">
          {/* Botão de tema na parte superior direita da foto */}
          <div className="absolute top-0 right-0 bg-white/30 dark:bg-black/30 p-2 rounded-lg backdrop-blur-sm shadow-sm border border-white/10 z-10">
            <ThemeToggle />
          </div>
          <img
            src="/app-icon.jpg"
            alt="Hub Machines"
            className="mx-auto mb-6 rounded-xl shadow-lg"
            style={{ height: '300px', width: 'auto' }}
          />
          <h2 className="text-4xl font-extrabold text-gray-900 dark:text-white drop-shadow-sm">
            Acesse sua conta
          </h2>
          <p className="mt-2 text-2xl font-bold text-gray-800 dark:text-gray-200 drop-shadow-md">
            Hub Machines: Repositório de Maquinas
          </p>
        </div>

        <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
          <div className="rounded-md shadow-sm -space-y-px">
            <div>
              <label htmlFor="email" className="sr-only">
                Email
              </label>
              <input
                id="email"
                name="email"
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="appearance-none rounded-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-black rounded-t-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 focus:z-10 sm:text-sm dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white"
                placeholder="Email"
              />
            </div>
            <div className="relative">
              <label htmlFor="password" className="sr-only">
                Senha
              </label>
              <input
                id="password"
                name="password"
                type={showPassword ? "text" : "password"}
                autoComplete="current-password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="appearance-none rounded-none relative block w-full px-3 py-2 pr-10 border border-gray-300 placeholder-gray-500 text-black rounded-b-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 focus:z-10 sm:text-sm dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white"
                placeholder="Senha"
              />
              <button
                type="button"
                className="absolute inset-y-0 right-0 pr-3 flex items-center"
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? (
                  <EyeSlashIcon className="h-5 w-5 text-gray-400" />
                ) : (
                  <EyeIcon className="h-5 w-5 text-gray-400" />
                )}
              </button>
            </div>
          </div>

          {error && (
            <div className="rounded-md bg-red-50 dark:bg-red-900/20 p-4">
              <div className="text-sm text-red-700 dark:text-red-400">
                {error}
              </div>
            </div>
          )}

          <div>
            <button
              type="submit"
              disabled={isLoading}
              className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed dark:focus:ring-offset-gray-900"
            >
              {isLoading ? <LoadingSpinner size="sm" className="mr-2" /> : null}
              {isLoading ? "Entrando..." : "Entrar"}
            </button>
          </div>
          
          <div className="mt-4">
            <button
              type="button"
              onClick={() => setShowWelcomeModal(true)}
              className="w-full flex justify-center py-2 px-4 border border-gray-300 dark:border-gray-600 text-sm font-medium rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 dark:focus:ring-offset-gray-900"
            >
              Sobre o Sistema
            </button>
          </div>
        </form>
      </div>
    </div>
    </>
  );
}
